import React, { useState } from "react";
import { PaypalIcon } from "icons/payments/PaypalIcon";
import { ApplePayIcon } from "icons/payments/ApplePayIcon";
import { GooglePayIcon } from "icons/payments/GooglePayIcon";
import { DollarSquareIcon } from "icons/payments/DollarSquareIcon";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { VisaIcon } from "icons/payments/VisaIcon";
import AmazonPayIcon from "icons/payments/AmazonPayIcon";
import { CrownCard } from "icons/dashboard/CrownCard";
import { DeleteCard } from "icons/dashboard/DeleteCard";
import CustomTooltip from "components/ui-component/CustomTooltip";
import PaymentMethodActionsMenu from "./PaymentMethodActionsMenu";
import { CrownMenuIcon } from "icons/payments/CrownMenuIcon";
import { EditMenuIcon } from "icons/payments/EditMenuIcon";
import { DeleteMenuIcon } from "icons/payments/DeleteMenuIcon";
import { EditCard } from "icons/dashboard/editCard";
import JCB from "../../icons/payments/JCB.svg";
import dinersClub from "../../icons/payments/dinersClub.svg";
import amex from "../../icons/payments/amex.svg";
import Discover from "icons/payments/Discover";
import UnionPay from "icons/payments/UnionPay";
import { maskEmail } from "utils/HelperFunctions";
import { useTid } from "utils/testid";

type Val = any;

interface Props {
  val: Val;
  idx: number;
  onDelete: () => void;
  onDefault: () => void;
  onEdit?: () => void;
  isMobile?: boolean;
  /** Renders the card without its delete / set-default controls, for places
   *  that only display the method (e.g. the dashboard summary). */
  readOnly?: boolean;
  /** Overrides the card's footprint. Defaults to the Payment Methods grid size;
   *  the dashboard passes the 374x250 slot from its own design. */
  sizeClassName?: string;
}

const PaymentMethodWalletCard: React.FC<Props> = ({
  val,
  idx,
  onDelete,
  onDefault,
  onEdit,
  isMobile = false,
  readOnly = false,
  sizeClassName = "h-[200px] xl:h-[150px] w-[350px] max-w-full xl:w-[260px]",
}) => {
  const tid = useTid();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const paymentMethodRaw = val?.payment_method;

  const paymentMethod =
    typeof paymentMethodRaw === "string" ? paymentMethodRaw.toLowerCase() : "";
  const pm = paymentMethod ?? "";

  const labelMap: Record<string, string> = {
    pp: "PayPal",
    ap: "Apple Pay",
    gp: "Google Pay",
    capp: "Cash App Pay",
    amzp: "Amazon Pay",
  };

  const label = labelMap[pm] ?? "";
  const walletId = val?.id || val?.payment_card_id || val?.account_email || idx;
  const walletLabel = `${label || "Wallet"} payment method`;

  const brandMarkClass = "max-h-[22px] xl:max-h-5 w-auto object-contain";
  const imgUrl = (): React.ReactNode => {
    const cardType = val?.cc_type;
    switch (cardType?.toLowerCase()) {
      case "visa":
        return <VisaIcon className={brandMarkClass} />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="AMEX" className={brandMarkClass} />;
      case "mc":
      case "master":
      case "mastercard":
        return <MastercardIcon className="h-[22px] xl:h-5 w-auto" />;
      case "discover":
      case "ds":
        return <Discover className={brandMarkClass} />;
      case "jcb":
        return <img src={JCB} alt="JCB" className={brandMarkClass} />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="DC" className={brandMarkClass} />;
      case "unionpay":
        return <UnionPay className={brandMarkClass} />;
      default:
        return "";
    }
  };

  return (
    <div
      role="group"
      aria-label={walletLabel}
      data-testid={tid(`wallet-method-${walletId}`)}
      className={`relative ${isMenuOpen ? "z-[5]" : ""} portalBackgroupBankWallet rounded-[16px] shadow-[0_12px_12px_rgba(15,38,92,0.17)] xl:rounded-[12px] xl:shadow-none xl:hover:!opacity-70 group ${sizeClassName} p-4 animate__animated animate__fadeIn`}
    >
      {!val?.available && (
        <div className="absolute top-0 left-0 w-full h-full rounded-[16px] xl:rounded-[12px] z-[1] portalBackgroupBankWalletUnavailable"></div>
      )}
      {val?.default_profile === "Y" && val?.available ? (
        <div className="absolute left-0 top-0 flex rounded-tl-[16px] xl:rounded-tl-[12px] z-[2]">
          <div className="bg-iconObscure w-3 h-5 rounded-tl-[16px] xl:rounded-tl-[12px]"></div>
          <div
            className="bg-iconObscure h-5 py-[2px] pr-[15px] text-white text-xs leading-normal font-medium flex items-center"
            style={{ clipPath: "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)" }}
          >
            Default
          </div>
        </div>
      ) : null}
      {!val?.available ? (
        <>
          <div className="absolute left-0 top-0 flex rounded-tl-[16px] xl:rounded-tl-[12px] z-[2]">
            <div className="bg-yellow w-3 h-5 rounded-tl-[16px] xl:rounded-tl-[12px]"></div>
            <div
              className="bg-yellow h-5 py-[2px] pr-[15px] text-white text-xs leading-normal font-medium flex items-center"
              style={{
                clipPath: val?.isExpired
                  ? "polygon(8% 0, 100% 0%, 90% 99%, 0% 100%)"
                  : "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
              }}
            >
              Unavailable
            </div>
          </div>
        </>
      ) : null}

      <div className="flex justify-center items-center flex-col gap-[9px] h-full">
        <div className="w-full grid place-items-center font-bold text-lg text-txtParagraph leading-[23.44px]">
          {label || ""}
        </div>
        {paymentMethod === "pp" ? (
          <PaypalIcon aria-hidden="true" />
        ) : paymentMethod === "ap" ? (
          <ApplePayIcon aria-hidden="true" />
        ) : paymentMethod === "gp" ? (
          <GooglePayIcon aria-hidden="true" />
        ) : paymentMethod === "amzp" ? (
          <AmazonPayIcon aria-hidden="true" />
        ) : (
          <DollarSquareIcon aria-hidden="true" />
        )}
        <div className="w-full grid place-items-center font-normal text-sm text-txtParagraph leading-[18.23px]">
          {paymentMethod === "pp" ? (
            <p className="w-[168px] truncate">
              {maskEmail(val?.paypalid) ?? ""}
            </p>
          ) : paymentMethod === "amzp" || paymentMethod === "capp" ? (
            <p className="w-[168px] truncate text-center">
              {maskEmail(val?.account_email) ?? ""}
            </p>
          ) : paymentMethod === "ap" || paymentMethod === "gp" ? (
            <div className="flex gap-[5px] justify-center items-center">
              {imgUrl()}
              <span> {val?.cc_4digit?.slice(-4)}</span>
            </div>
          ) : (
            <span className="invisible"> text </span>
          )}
        </div>
      </div>
      {!readOnly && (
        <PaymentMethodActionsMenu
          testId={`wallet-method-${walletId}`}
          triggerLabel={`Actions for ${walletLabel}`}
          tone="onLight"
          onOpenChange={setIsMenuOpen}
          actions={[
            ...(!val?.isExpired &&
            val?.default_profile !== "Y" &&
            val?.available
              ? [
                  {
                    key: "set-default",
                    label: "Set as Default",
                    icon: <CrownMenuIcon />,
                    onClick: onDefault,
                  },
                ]
              : []),
            ...(paymentMethod === "gp" && onEdit
              ? [
                  {
                    key: "edit",
                    label: "Update Google Pay",
                    icon: <EditMenuIcon />,
                    onClick: onEdit,
                  },
                ]
              : []),
            {
              key: "delete",
              label: "Delete Wallet",
              icon: <DeleteMenuIcon />,
              onClick: onDelete,
              isDestructive: true,
              dividerBefore:
                Boolean(onEdit && paymentMethod === "gp") ||
                (!val?.isExpired &&
                  val?.default_profile !== "Y" &&
                  val?.available),
            },
          ]}
        />
      )}

      <div
        className={`absolute bottom-2 z-[2] right-2 cursor-pointer hidden gap-2 ${
          readOnly ? "" : "xl:group-hover:flex"
        }`}
      >
        <div
          role="button"
          tabIndex={0}
          aria-label={`Delete ${walletLabel}`}
          data-testid={tid(`wallet-method-${walletId}-delete-button`)}
          onClick={onDelete}
        >
          <CustomTooltip
            maxwidth="430px"
            place="top"
            id={`wallet-delete-${walletId}`}
            content={`<div class="text-xs leading-[18px] font-normal">Delete</div>`}
            disabled={isMobile}
          >
            <DeleteCard
              id={`wallet-delete-${walletId}`}
              height="34"
              width="34"
              aria-hidden="true"
            />
          </CustomTooltip>
        </div>
        {paymentMethod === "gp" && onEdit ? (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Update ${walletLabel}`}
            data-testid={tid(`wallet-method-${walletId}-edit-button`)}
            onClick={onEdit}
          >
            <CustomTooltip
              maxwidth="430px"
              place="top"
              id={`wallet-edit-${walletId}`}
              content={`<div class="text-xs leading-[18px] font-normal">Update</div>`}
              disabled={isMobile}
            >
              <EditCard
                id={`wallet-edit-${walletId}`}
                height="34"
                width="34"
                aria-hidden="true"
              />
            </CustomTooltip>
          </div>
        ) : null}
        {!val?.isExpired && val?.default_profile !== "Y" && val?.available ? (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Set ${walletLabel} as default`}
            data-testid={tid(`wallet-method-${walletId}-default-button`)}
            onClick={onDefault}
          >
            <CustomTooltip
              maxwidth="430px"
              place="top"
              id={`wallet-default-${walletId}`}
              content={`<div class="text-xs leading-[18px] font-normal w-[80px]">Set as default</div>`}
              disabled={isMobile}
            >
              <CrownCard
                id={`wallet-default-${walletId}`}
                height="34"
                width="34"
                aria-hidden="true"
              />
            </CustomTooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const VisaOrCardIcon: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return null;
  const t = type.toLowerCase();
  if (t === "visa") return <VisaIcon aria-hidden="true" />;
  if (t === "mc" || t === "mastercard" || t === "master")
    return <MastercardIcon aria-hidden="true" />;
  if (t === "amex" || t=== "american express" || t === "americanexpress" || t === "american expres")
    return <img src={amex} alt="amex" className="object-contain" />;
  if (t === "discover" || t === "ds") return <Discover aria-hidden="true" />;
  if (t === "jcb") return <img src={JCB} alt="jcb" />;
  if (t === "diners" || t === "dinersclub" || t === "dc" || t === "diners club")
    return <img src={dinersClub} alt="diners" />;
  if (t === "unionpay") return <UnionPay aria-hidden="true" />;
  return null;
};

export default PaymentMethodWalletCard;
